/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import startCase from 'lodash/startCase';

import {
    Anchor,
    Banner,
    Button,
    ButtonGroup,
    CloseButton,
    Flex,
    IconPlusCircle,
    IconGrid,
    IconGridOutline,
    IconList,
    IconListOutline,
    Spacer,
    Tabs,
    Text,
    TextButton,
} from '@zillow/constellation';
import { Container, Row, Col } from '@zillow/zrs-comet';
import ZRMUtils from '@zg-rentals/zrm-utils-js/lib/utils';

import { AddPropertyModal } from 'app/shared/components/Modal';
import { RMWContext } from 'app/shared/providers/RMWContext';
import { withErrorBoundary } from 'app/shared/components/ErrorBoundary';
import ActionMessageBox from 'app/shared/components/properties/ActionMessageBox';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import ApplicationActions from 'app/shared/actions/ApplicationActions';
import appLogger from 'app/shared/loggers/appLogger';
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';
import BannerMaintenance from 'app/shared/components/BannerMaintenance';
import Constants from 'app/shared/constants';
import EmailVerificationActions from 'app/shared/actions/EmailVerificationActions';
import HeadWrapper from 'app/shared/components/HeadWrapper';
import InfiniteScroller from 'app/shared/components/InfiniteScroller';
import ModalActions from 'app/shared/actions/ModalActions';
import ModalConstants from 'app/shared/constants/ModalConstants';
import NotificationModule from 'app/client/workers/notificationModule';
import PendingEmailVerificationBox from 'app/shared/components/properties/PendingEmailVerificationBox';
import PropertiesActions from 'app/shared/actions/PropertiesActions';
import PropertiesConstants from 'app/shared/constants/PropertiesConstants';
import PropertiesDisplay from 'app/shared/components/properties/PropertiesDisplay';
import PropertiesPageSkeleton from 'app/shared/components/skeletons/PropertiesPageSkeleton';
import QueryConstants from 'app/shared/constants/QueryConstants';
import queryUtils from 'app/shared/utils/queryUtils';
import SearchBox from 'app/shared/components/SearchBox';
import StringConstants from 'app/shared/constants/StringConstants';
import TermsAndConditionsActions from 'app/shared/actions/TermsAndConditionsActions';
import {
    AppSelectors,
    EmailVerificationSelectors,
    ModalSelectors,
    PropertiesSelectors,
    RoutingSelectors,
    UserSelectors,
} from 'app/shared/selectors';

import accessUtils from 'app/shared/utils/accessUtils';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import contextUtils from 'app/shared/utils/contextUtils';
import userUtils from 'app/shared/utils/userUtils';
import numberUtils from 'app/shared/utils/numberUtils';
import listingClaimUtils from 'app/shared/utils/listingClaimUtils';
import postingUtils from 'app/shared/utils/postingUtils';

import {
    StyledCloseButton,
    StyledFlexContainer,
    StyledHeading,
    StyledHeadingContainer,
    StyledSearchBoxContainer,
    StyledBrandBlueButton,
    StyledRightSubFlexContainer,
    StyledTabsList,
    StyledTab,
} from './StyledComponents';

import './style.scss';

const defaultFilterData = {
    includeActive: Constants.PROPERTIES_ACTIVE_DEFAULT,
    includeInactive: Constants.PROPERTIES_INACTIVE_DEFAULT,
    searchText: Constants.PROPERTIES_SEARCHTEXT_DEFAULT,
    feeds: Constants.PROPERTIES_FEEDS_DEFAULT,
    manual: Constants.PROPERTIES_MANUAL_DEFAULT,
    ascending: Constants.PROPERTIES_ASCENDING_DEFAULT,
    sort: Constants.PROPERTIES_SORT_DEFAULT,
    featured: Constants.PROPERTIES_FEATURED_DEFAULT,
    startKeyExclusive: Constants.PROPERTIES_START_KEY_EXCLUSIVE_DEFAULT,
    limit: Constants.PROPERTIES_LIMIT_DEFAULT,
    includeArchived: Constants.PROPERTIES_INCLUDE_ARCHIVED,
    includeUnarchived: Constants.PROPERTIES_INCLUDE_UNARCHIVED,
    viewType: PropertiesConstants.PROPERTIES_VIEW_TYPE.GRID,
    subNavFilterType: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
};

// TODO: quick fix ZRM-7500, might need to revisit
const MIN_PROPERTIES_FOR_SEARCH_DISPLAY = 0;
const { GA_EVENTS: { APPLICATIONS_MODAL_DISMISS } } = AnalyticsConstants;
// eslint-disable-next-line max-len
const feedClaimAndConvertBannerText = "Our timeline for feed transition for your MLS has shifted, you've still got time to claim your rental listings and keep them on Zillow, Trulia and HotPads. If you do not claim and convert your rental listings, you'll have to recreate them once your MLS has transitioned.";

const DEFAULT_UPDATE = {
    includeArchived: Constants.PROPERTIES_INCLUDE_ARCHIVED,
    includeUnarchived: Constants.PROPERTIES_INCLUDE_UNARCHIVED,
    searchText: Constants.PROPERTIES_SEARCHTEXT_DEFAULT,
    startKeyExclusive: Constants.PROPERTIES_START_KEY_EXCLUSIVE_DEFAULT,
};

class PropertiesPage extends React.Component {
    static contextType = RMWContext;

    static contextTypes = {
        brand: PropTypes.string,
        paths: PropTypes.object,
    };


    constructor(props) {
        super(props);

        const {
            emailVerification,
            location,
            properties: { numTotalListings, numActiveListings },
            isScreenTabletOrDesktop,
            isEnterpriseCustomer,
        } = props;

        const queryParams = ZRMUtils.queryUtils.parse(location.search);
        const { archived = false, startKeyExclusive = '', searchText = '' } = queryParams;
        const includeArchived = archived === 'true';

        defaultFilterData.includeArchived = includeArchived;
        defaultFilterData.includeUnarchived = !includeArchived;
        if (isEnterpriseCustomer && numActiveListings > 0 && !includeArchived) {
            defaultFilterData.includeInactive = false;
        }
        if (isEnterpriseCustomer && numActiveListings > 0 && !includeArchived) {
            defaultFilterData.subNavFilterType = PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET;
        }
        if (isEnterpriseCustomer || (isScreenTabletOrDesktop && numTotalListings >= 10)) {
            defaultFilterData.viewType = PropertiesConstants.PROPERTIES_VIEW_TYPE.LIST;
        }

        // initial state
        this.state = {
            displayApplicationsNotification: false,
            displayEmailVerificationNotification: false,
            displayPaymentsNotification: false,
            displayLeasesNotification: false,
            displayPendingEmailVerificationBox: emailVerification.hasPendingEmailVerification,
            displayListingClaimCompleteBanner: queryParams.itc === 'listing_claim_complete',
            listingsLoading: true,
            totalListingsWithoutFilters: null,
            queryParams,
            isDebugMode: queryParams.debug === 'true',
            emailVerificationNotificationText: '',
            displayTimedOutMessage: false,
            displayNoListingsFoundMessage: true,
            disableLoadMore: false,
            didPropertiesLoad: false,
            isSearchBarTouched: false,
            searchText: searchText || '',
            startKeyExclusive: startKeyExclusive || '',
        };

        // bind methods
        this.checkNotificationDisplays = this.checkNotificationDisplays.bind(this);
        this.checkInitialDisplayModal = this.checkInitialDisplayModal.bind(this);
        this.handleEnableApplicationsBtnClick = this.handleEnableApplicationsBtnClick.bind(this);
        this.handleNotificationCloseClick = this.handleNotificationCloseClick.bind(this);
        // eslint-disable-next-line max-len
        this.handleEmailVerificationNotificationCloseClick = this.handleEmailVerificationNotificationCloseClick.bind(this);
        this.handleLoadMoreClick = this.handleLoadMoreClick.bind(this);
        this.handlePaymentsNotificationCloseClick = this.handlePaymentsNotificationCloseClick.bind(this);
        this.handleResendEmailClick = this.handleResendEmailClick.bind(this);
        this.handleAddPropertyClick = this.handleAddPropertyClick.bind(this);
        this.handlePushNotificationClick = this.handlePushNotificationClick.bind(this);
        this.handleLeasesNotificationCloseClick = this.handleLeasesNotificationCloseClick.bind(this);
        this.onSearchFilterUpdate = this.onSearchFilterUpdate.bind(this);
        this.renderLoadComplete = this.renderLoadComplete.bind(this);
        this.renderListingsLoadComplete = this.renderListingsLoadComplete.bind(this);
        this.getConversionPercentage = this.getConversionPercentage.bind(this);
        this.showAddPropertyModal = this.showAddPropertyModal.bind(this);
        this.updatePaymentsNotificationDisplay = this.updatePaymentsNotificationDisplay.bind(this);
        this.updateLeasesNotificationDisplay = this.updateLeasesNotificationDisplay.bind(this);
        // archived
        this.toggleArchivedView = this.toggleArchivedView.bind(this);
        this.getListings = this.getListings.bind(this);
        this.isZillow = this.isZillow.bind(this);
        this.renderSubNavButton = this.renderSubNavButton.bind(this);
        this.subNavFiltersOnSelectHandler = this.subNavFiltersOnSelectHandler.bind(this);
        this.handleViewSwitcherClick = this.handleViewSwitcherClick.bind(this);
    }

    componentWillMount() {
        if (__SERVER__) {
            return;
        }
        const {
            dispatch,
            isUserBlocked,
            hasSubNavFiltersBeenTouched,
            propertiesFilters: { searchText },
        } = this.props;

        const {
            queryParams,
        } = this.state;

        if (!hasSubNavFiltersBeenTouched) {
            dispatch(PropertiesActions.updateFilters({
                ...defaultFilterData,
            }));
        }

        const securityToken = queryParams[QueryConstants.SECURITY_TOKEN];

        if (!isUserBlocked && securityToken) {
            dispatch(EmailVerificationActions.verifySecurityToken(securityToken))
                .then((response) => {
                    const apiResponse = response.response;
                    let emailVerificationNotificationText = 'Your email has been verified!';

                    /**
                     *  When successful:
                     *  {
                     *      status: 200,
                     *      success: true
                     *  }
                     *
                     *  On failure:
                     *  {
                     *      status: 400,
                     *      success: false,
                     *      response: {
                     *          errorCode: 'email-already-verified',
                     *          message: 'Your email has already been verified'
                     *      }
                     *  }
                     */
                    if (apiResponse &&
                        apiResponse.errorCode &&
                        apiResponse.errorCode === 'email-already-verified') {
                        emailVerificationNotificationText = apiResponse.message;
                    }

                    this.setState({
                        displayEmailVerificationNotification: true,
                        displayPendingEmailVerificationBox: false,
                        emailVerificationNotificationText,
                    });

                    return this.getListings(true);
                })
                .catch(() => {
                    dispatch(ModalActions.showModal({
                        modalType: ModalConstants.VERIFY_EMAIL_FAILURE_MODAL,
                        header: 'Incorrect account',
                    }));
                    return this.getListings(true);
                });
        } else if (searchText) {
            /*
                * if searchText is inside propertiesFilters, then reconstruct the navigation path
            */
            this.onSearchFilterUpdate(searchText);
        } else {
            /*
                * get the correct properties for user based on the url
                * if archived is true in query params, fetch archived properties and set the component
                * to show archived title
            */
            const update = { ...DEFAULT_UPDATE };

            if (queryParams.searchText) {
                update.searchText = queryParams.searchText;
                this.setState({
                    searchText: queryParams.searchText,
                    isSearchBarTouched: true,
                });
            }

            // check for archived query string
            if (queryParams.archived === 'true') {
                update.includeArchived = true;
                update.includeUnarchived = false;

                this.updateFiltersAndListings(update);
            } else {
                update.includeArchived = false;
                update.includeUnarchived = true;

                this.updateFiltersAndListings(update);
            }

            this.getListings(true, !isUserBlocked);
        }
    }

    componentDidMount() {
        const { dispatch, isUserBlocked } = this.props;

        // always close the add property modal
        dispatch(ModalActions.updateDisplayAddPropertyModal({ isOpen: false }));

        // check if user is blocked
        if (!isUserBlocked) {
            this.checkNotificationDisplays();
        }

        this._isMounted = true;

        dispatch(PropertiesActions.getListingConversionStatus());
    }

    componentWillReceiveProps(nextProps) {
        const {
            dispatch,
            location,
            isScreenTabletOrDesktop,
            isEnterpriseCustomer,
            propertiesFilters: { viewType },
        } = this.props;

        // on same page but searchText was changed
        if ((location.pathname === nextProps.location.pathname) && (location.search !== nextProps.location.search)) {
            const parsedQueryString = ZRMUtils.queryUtils.parse(nextProps.location.search);
            const { archived = false, startKeyExclusive = '', searchText = '' } = parsedQueryString;

            if (this.SearchBoxInstance) {
                this.setState({
                    searchText,
                    isSearchBarTouched: true,
                });
                this.SearchBoxInstance.updateSearchText(searchText);
            }

            const isArchived = archived === 'true';
            const update = {
                includeArchived: isArchived,
                includeUnarchived: !isArchived,
                searchText,
                startKeyExclusive,
            };

            this.updateFiltersAndListings(update);
        }

        // if regularUser, then switch to grid when screen gets too small
        if (!isEnterpriseCustomer && isScreenTabletOrDesktop !== nextProps.isScreenTabletOrDesktop
            && !nextProps.isScreenTabletOrDesktop
            && viewType === PropertiesConstants.PROPERTIES_VIEW_TYPE.LIST) {
            dispatch(PropertiesActions.updateFilters({ viewType: PropertiesConstants.PROPERTIES_VIEW_TYPE.GRID }));
        }
    }

    // check if we should display notification banner
    checkNotificationDisplays() {
        const { location } = this.props;
        const { search } = location;
        const isFromMarketingPaymentsRoute = contextUtils.isFromMarketingPaymentsRoute(search);
        const isFromMarketingLeasesRoute = contextUtils.isFromMarketingLeasesRoute(search);

        if (isFromMarketingPaymentsRoute) {
            this.updatePaymentsNotificationDisplay(true);
        } else if (isFromMarketingLeasesRoute) {
            this.updateLeasesNotificationDisplay(true);
        }
    }

    checkInitialDisplayModal() {
        const { brand, paths } = this.context;
        const { totalListingsWithoutFilters, queryParams } = this.state;
        const {
            cookieEnabledServices,
            dispatch,
            properties,
            emailVerification,
            roles,
            location,
        } = this.props;

        const { search } = location;

        const { hasFeedListings, hasPendingEmailVerification } = emailVerification;
        const isPostingPathAdmin = userUtils.isPostingPathAdmin(roles);

        const isFromMarketingPaymentsRoute = contextUtils.isFromMarketingPaymentsRoute(search);
        const isFromMarketingApplicationsRoute = contextUtils.isFromMarketingApplicationsRoute(search);
        const isFromMarketingLeasesRoute = contextUtils.isFromMarketingLeasesRoute(search);

        // if any marketing campaign
        if (isFromMarketingPaymentsRoute || isFromMarketingApplicationsRoute || isFromMarketingLeasesRoute) {
            const marketingRoute = postingUtils.getDestinationTab(paths, search);

            // if total listings = 0
            if (totalListingsWithoutFilters === 0) {
                this.showAddPropertyModal();

                // track
                const dimension = AnalyticsConstants.Z_CUSTOM_DIMENSION.EVENT_CONTENT;
                analyticsUtils.setCustomDimension(`MarketingPath${startCase(marketingRoute)}`, dimension, brand);
                dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.ADD_PROPERTY_MODAL));
                return;
            }

            // if 1 listing go to PDP (app or payments)
            if (totalListingsWithoutFilters === 1) {
                const { listingId } = properties.listings[0];
                const targetRoute = `${paths.propertiesPage}/${listingId}/${marketingRoute}${location.search}`;

                // nav to pdp
                window.location = targetRoute;
                return;
            }

            // if total listings > 1 and from applications campaign
            if (isFromMarketingApplicationsRoute && totalListingsWithoutFilters > 1) {
                // check if any listing can access applications
                let foundListingWithApplicationAccess = false;

                for (let i = 0; i < properties.listings.length; i++) {
                    const { restrictions } = properties.listings[i];

                    const canAccessApplicationsResponse = accessUtils.canAccessApplications({
                        brand,
                        restrictions,
                        cookieEnabledServices,
                        groupState: properties.listings[i].state,
                    });

                    if (canAccessApplicationsResponse) {
                        foundListingWithApplicationAccess = true;
                        break;
                    }
                }

                // found a listing
                if (foundListingWithApplicationAccess) {
                    dispatch(ModalActions.showModal({
                        modalType: ModalConstants.ENABLE_APPLICATIONS_MODAL,
                        header: 'Enable applications',
                        handleConfirmClick: this.handleEnableApplicationsBtnClick,
                        onClose: () => {
                            dispatch(analyticsUtils.track.GAEventV2(APPLICATIONS_MODAL_DISMISS));
                        },
                    }));
                    return;
                }
            }
        }

        // if coming from zillow landing page or hotpads post a listing link
        if (queryParams[QueryConstants.POSTING_PATH] === 'true'
            || (queryParams[QueryConstants.SIGN_IN_LP] === 'true'
            && totalListingsWithoutFilters === 0)) {
            this.showAddPropertyModal();

            // track only if coming from Get Started button on landing page
            if (queryParams[QueryConstants.POSTING_PATH] === 'true' &&
                brand === Constants.UNFORMATTED_BRAND_NAMES.zillow
            ) {
                const dimension = AnalyticsConstants.Z_CUSTOM_DIMENSION.EVENT_CONTENT;
                analyticsUtils.setCustomDimension('LandingPageGetStarted', dimension, brand);

                dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.ADD_PROPERTY_MODAL));
            }
            return;
        }

        // if has feed listings and should NOT see add property modal
        //     (Add property modal has higher priority. The user will see
        //     the email verification modal to see feed listings next time they see
        //     properties page)
        if (queryParams[QueryConstants.EMAIL_VERIFICATION] === 'true' ||
            (hasFeedListings && !hasPendingEmailVerification)) {
            if (isPostingPathAdmin) {
                dispatch(ModalActions.showModal({
                    modalType: ModalConstants.VERIFY_EMAIL_MODAL,
                    header: 'Listings found',
                    handleResendEmailClick: this.handleResendEmailClick,
                }));
                this.setState({
                    displayPendingEmailVerificationBox: true,
                });
            } else if (!hasPendingEmailVerification) {
                dispatch(EmailVerificationActions.sendSecurityToken({ emailVerificationType: 'claimFeedListings' }))
                    .then((response) => {
                        if (response.success) {
                            dispatch(EmailVerificationActions.updateEmailVerificationInfo({
                                hasPendingEmailVerification: true,
                            }));

                            dispatch(ModalActions.showModal({
                                modalType: ModalConstants.VERIFY_EMAIL_MODAL,
                                header: 'Listings found',
                                handleResendEmailClick: this.handleResendEmailClick,
                            }));
                            this.setState({
                                displayPendingEmailVerificationBox: true,
                            });
                        }
                    });
            }
        }
    }

    getListingsCallTimeout(response = {}) {
        let isTimeout = false;

        if (response.error) {
            const error = response.error.toString();

            isTimeout = error.indexOf('timeout') !== -1;
        }

        return isTimeout;
    }

    getListings(isFirstFetch = false, checkInitialDisplayModal = false, appendToListings = false) {
        const {
            cookieEnabledServices,
            dispatch,
            appConfig,
        } = this.props;

        const useV2API = accessUtils.canAccessConnectedExperience({
            appConfig,
            cookieEnabledServices,
        });

        return dispatch(PropertiesActions.getListings(appendToListings, useV2API))
            .then((response) => {
                this.setState({
                    listingsLoading: false,
                    displayTimedOutMessage: this.getListingsCallTimeout(response),
                    didPropertiesLoad: true,
                    disableLoadMore: false,
                });

                if (isFirstFetch && response.payload) {
                    const { listingsModel } = response.payload;

                    this.setState({
                        totalListingsWithoutFilters: listingsModel.numTotalListings,
                    }, () => {
                        // complete
                        if (checkInitialDisplayModal) {
                            this.checkInitialDisplayModal();
                        }
                    });
                }
            })
            .catch((err) => {
                this.setState({
                    listingsLoading: false,
                    error: err,
                    didPropertiesLoad: false,
                    disableLoadMore: true,
                });
            });
    }

    isZillow() {
        const { brand } = this.context;
        return brand === StringConstants.UNFORMATTED_BRAND_NAMES.zillow;
    }

    onSearchFilterUpdate(value) {
        const { propertiesFilters: { includeArchived } } = this.props;
        const update = {};

        update.searchText = value;
        update.startKeyExclusive = '';

        // this is passed to let createQueryObjectAndRedirectTo know that it is a search operation
        // and bypass the router transition
        const isSearchFilterUpdate = true;

        // set state to preserve the search bar on the UI
        // if isSearchBarTouched is true it will always show search bar
        this.setState({
            isSearchBarTouched: true,
            searchText: value,
        });

        this.createQueryObjectAndRedirectTo(includeArchived, value, '', isSearchFilterUpdate);
    }

    toggleArchivedView() {
        const { paths } = this.context;

        const {
            dispatch,
            propertiesFilters: { includeArchived, subNavFilterType },
        } = this.props;

        // if showArchive is true, append filter params to the url
        const route = includeArchived ? '' : '?archived=true';

        // is in archivedView and entUser
        if (includeArchived) {
            // going back to regular listings view
            const isOnMarket = subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET;
            const isOffMarket = subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET;
            const update = {
                includeActive: !isOffMarket,
                includeInactive: !isOnMarket,
            };
            dispatch(PropertiesActions.updateFilters(update));
        } else {
            // going to archived listings view
            dispatch(PropertiesActions.updateFilters({ includeActive: true, includeInactive: true }));
        }

        // clear searchbox text
        if (this.SearchBoxInstance) {
            this.SearchBoxInstance.updateSearchText('');
        }

        // no need to call updateFiltersAndListings since route change => componentWillReceiveProps
        window.router.transitionTo(
            `${paths.propertiesPage}${route}`,
        );
    }

    showAddPropertyModal() {
        const { dispatch, user } = this.props;

        if (user.features.canAddListing) {
            dispatch(ModalActions.updateDisplayAddPropertyModal({ isOpen: true }));
        }
    }

    updateFiltersAndListings(
        update,
        appendToListings = false,
        displayNoListingsFoundMessage = true,
        showLoading = true,
        disableLoadMore = false
    ) {
        const { dispatch } = this.props;

        this.setState({
            listingsLoading: showLoading,
            didPropertiesLoad: !showLoading,
            disableLoadMore,
            displayNoListingsFoundMessage,
        });

        dispatch(PropertiesActions.updateFilters(update));

        if (this._isMounted) {
            this.getListings(false, false, appendToListings);
        }
    }

    updatePaymentsNotificationDisplay(doShow = true) {
        this.setState({ displayPaymentsNotification: doShow });
    }

    updateLeasesNotificationDisplay(doShow = true) {
        this.setState({ displayLeasesNotification: doShow });
    }

    handleEnableApplicationsBtnClick() {
        const { dispatch } = this.props;

        dispatch(ApplicationActions.enableApplications()).then(() => {
            dispatch(TermsAndConditionsActions.markAcceptedTerms());
            dispatch(ModalActions.hideModal());
            this.setState({
                displayApplicationsNotification: true,
            });
        });
    }

    handleAddPropertyClick() {
        const { dispatch, isUserBlocked } = this.props;
        const { brand } = this.context;

        if (!isUserBlocked) {
            this.showAddPropertyModal();

            // track
            if (brand === Constants.UNFORMATTED_BRAND_NAMES.zillow) {
                const dimension = AnalyticsConstants.Z_CUSTOM_DIMENSION.EVENT_CONTENT;
                analyticsUtils.setCustomDimension('MyPropertiesAddProperty', dimension, brand);

                dispatch(analyticsUtils.track.GAEventV2(
                    AnalyticsConstants.GA_EVENTS.ADD_PROPERTY_MODAL,
                ));
            }
        }
    }

    handleNotificationCloseClick() {
        this.setState({ displayApplicationsNotification: false });
    }

    handleEmailVerificationNotificationCloseClick() {
        this.setState({ displayEmailVerificationNotification: false });
    }

    handleListingClaimBannerCloseClick() {
        this.setState({ displayListingClaimCompleteBanner: false });
    }

    handleLoadMoreClick(startKeyExclusive) {
        const {
            propertiesFilters: { includeArchived, searchText },
        } = this.props;

        const update = {
            includeArchived,
            includeUnarchived: !includeArchived,
            searchText,
            startKeyExclusive,
        };
        this.updateFiltersAndListings(update, true, false, false, true);
    }

    createQueryObjectAndRedirectTo(
        includeArchived = false,
        searchText = '',
        startKeyExclusive = '',
        isSearchFilterUpdate = false
    ) {
        const queryString = this.createQueryString(includeArchived, searchText, startKeyExclusive);

        // replace the url only if its a search filter update
        if (isSearchFilterUpdate) {
            window.router.redirectTo({
                pathname: this.props.location.pathname,
                search: queryString,
            });
        }
    }

    createQueryString(includeArchived = false, searchText = '', startKeyExclusive = '') {
        const queryObject = {};

        if (includeArchived) {
            queryObject.archived = includeArchived;
        }
        queryObject.startKeyExclusive = startKeyExclusive;
        queryObject.searchText = searchText;

        return queryUtils.stringify(queryObject);
    }

    handlePaymentsNotificationCloseClick() {
        this.updatePaymentsNotificationDisplay(false);
    }

    handleLeasesNotificationCloseClick() {
        this.updateLeasesNotificationDisplay(false);
    }

    handleResendEmailClick(text) {
        this.setState({
            emailVerificationNotificationText: text,
            displayEmailVerificationNotification: true,
            displayPendingEmailVerificationBox: false,
        });
    }

    getFullAddress(queryParams) {
        const {
            street = '',
            city = '',
            state = '',
            zip = '',
        } = queryParams;

        const fullAddress = `${street} ${city}, ${state} ${zip}`;

        return fullAddress.replace(/\s{2,}/g, ' ').trim();
    }

    needsRedirect() {
        const { emailVerification: { hasFeedListings, hasPendingEmailVerification } } = this.props;
        const { totalListingsWithoutFilters, queryParams } = this.state;

        const bypassRedirect = hasFeedListings && hasPendingEmailVerification;

        if (!this.isZillow() || totalListingsWithoutFilters !== 0 || bypassRedirect) {
            return false;
        }

        return (
            queryParams[QueryConstants.POSTING_PATH] !== 'true' &&
            queryParams[QueryConstants.SIGN_IN_LP] !== 'true' &&
            queryParams[QueryConstants.EMAIL_VERIFICATION] !== 'true' &&
            !queryParams[QueryConstants.MARKETING_PATH] &&
            !queryParams[QueryConstants.SECURITY_TOKEN] &&
            !queryParams[QueryConstants.ARCHIVED]
        );
    }

    renderMaintenanceBanner() {
        return (
            <Container data-test="MaintenanceBanner-container">
                <Row>
                    <Col>
                        <BannerMaintenance />
                    </Col>
                </Row>
            </Container>
        );
    }

    renderPageHeading() {
        const {
            properties: { numTotalListings: totalPropertiesShown },
            propertiesFilters: { includeArchived },
            user,
            cookieEnabledServices,
            appConfig,
        } = this.props;
        const { isSearchBarTouched } = this.state;

        const pageTitle = includeArchived ? 'Archived Properties' : 'Properties';
        const canFeedConnectCreateListing = accessUtils
            .canFeedConnectCreateListing({ appConfig, cookieEnabledServices });
        const showAddPropertyButton = user.features.canAddListing
            || (user.isSingleFamilyEnterpriseCustomer && canFeedConnectCreateListing && user.features.canAddListing);

        return (
            <StyledHeadingContainer
                fluidMobile={true}
                marginBottom={2}
            >
                {(totalPropertiesShown > 0 || isSearchBarTouched) && (
                    <Row align="center" justify="between">
                        <Col>
                            <StyledHeading
                                level="3"
                                marginRight="sm"
                                serif={true}
                            >
                                {pageTitle}
                            </StyledHeading>
                        </Col>

                        <Col align="flex-end">
                            {showAddPropertyButton && (
                                <StyledBrandBlueButton
                                    fontType="bodySmallHeading"
                                    onClick={this.handleAddPropertyClick}
                                >
                                    <Spacer marginRight="xs" as="span">
                                        <IconPlusCircle size="sm" />
                                    </Spacer>
                                    Add a property
                                </StyledBrandBlueButton>
                            )}
                        </Col>
                    </Row>
                )}
            </StyledHeadingContainer>
        );
    }

    renderSubNavButton(filterOption) {
        const { propertiesFilters: { subNavFilterType } } = this.props;
        const { listingsLoading } = this.state;

        /**
         * @param {boolean} [id] - ID of the tab
         * @param {String} [label] - Label of the button
         * @param {Number} [count] - Number display after the label
         * */
        const {
            id,
            label,
            count,
        } = filterOption;

        return (
            <StyledTab
                key={id}
                id={id}
                isActive={id === subNavFilterType}
                disabled={listingsLoading}
            >
                {`${label} (${count})`}
            </StyledTab>
        );
    }

    subNavFiltersOnSelectHandler(subNavFilterOption) {
        const { dispatch } = this.props;
        const { includeActive, includeInactive, id: subNavFilterType } = subNavFilterOption;
        const startKeyExclusive = Constants.PROPERTIES_START_KEY_EXCLUSIVE_DEFAULT;

        const update = {
            includeActive, includeInactive, subNavFilterType, startKeyExclusive,
        };

        dispatch(PropertiesActions.updateFiltersTouched(true));
        this.updateFiltersAndListings(update);
    }

    handleViewSwitcherClick(viewType) {
        const { dispatch } = this.props;

        dispatch(PropertiesActions.updateFiltersTouched(true));
        dispatch(PropertiesActions.updateFilters({ viewType }));
    }

    renderSubHeadingSection() {
        const { isSearchBarTouched, searchText } = this.state;
        const {
            properties: {
                numActiveListings,
                numInactiveListings,
                numTotalListings: totalPropertiesShown,
            },
            propertiesFilters: { includeArchived, viewType, subNavFilterType },
            isEnterpriseCustomer,
            isScreenTabletOrDesktop,
        } = this.props;

        const isGridView = viewType === PropertiesConstants.PROPERTIES_VIEW_TYPE.GRID;
        const showViewTypeToggle = isEnterpriseCustomer || isScreenTabletOrDesktop;
        const placeholderText = (isEnterpriseCustomer && !isScreenTabletOrDesktop) ?
            'City, address or ZIP' : 'Search by city, address and ZIP code';

        const subNavFilterOptions = {
            /**
             * @param {boolean} [id] - ID of the tab
             * @param {boolean} [includeActive] - Include active listings
             * @param {boolean} [includeInactive] - Include inactive listings
             * @param {String} [label] - Label of the button
             * @param {Number} [count] - Number display after the label
             * */
            [PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL]: {
                id: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
                includeActive: true,
                includeInactive: true,
                label: 'All',
                count: totalPropertiesShown,
            },
            [PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET]: {
                id: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET,
                includeActive: true,
                includeInactive: false,
                label: 'For rent',
                count: numActiveListings,
            },
            [PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET]: {
                id: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET,
                includeActive: false,
                includeInactive: true,
                label: 'Off-market',
                count: numInactiveListings,
            },
        };

        return (
            <Container
                fluidMobile={true}
                className="SubHeadingSection"
                marginBottom={(!isGridView && !isScreenTabletOrDesktop) ? '0' : 'md'}
            >
                { (isSearchBarTouched || totalPropertiesShown >= MIN_PROPERTIES_FOR_SEARCH_DISPLAY) && (
                    <StyledFlexContainer
                        className="SubHeadingSection-Row"
                        display="flex"
                        alignItems="center"
                        paddingX="sm"
                        paddingY="sm"
                    >
                        <StyledRightSubFlexContainer>
                            <StyledSearchBoxContainer>
                                <SearchBox
                                    className="SubHeaderingSection-SearchBox"
                                    ref={(instance) => {
                                        this.SearchBoxInstance = instance;
                                    }}
                                    placeholder={placeholderText}
                                    onSearch={this.onSearchFilterUpdate}
                                    initialText={searchText}
                                />
                            </StyledSearchBoxContainer>

                            {showViewTypeToggle && (
                                <Spacer marginLeft="sm">
                                    <ButtonGroup
                                        aria-label="Properties switcher button-group"
                                        conjoined={true}
                                    >
                                        <Button
                                            onClick={() => this.handleViewSwitcherClick(
                                                PropertiesConstants.PROPERTIES_VIEW_TYPE.LIST
                                            )}
                                            buttonType={isGridView ? 'tertiary' : 'primary'}
                                        >
                                            {isGridView ?
                                                <IconListOutline className="PropertiesPage-ListingViewSwitcher" /> :
                                                <IconList className="PropertiesPage-ListingViewSwitcher" />
                                            }
                                        </Button>
                                        <Button
                                            onClick={() => this.handleViewSwitcherClick(
                                                PropertiesConstants.PROPERTIES_VIEW_TYPE.GRID
                                            )}
                                            buttonType={isGridView ? 'primary' : 'tertiary'}
                                        >
                                            {isGridView ?
                                                <IconGrid className="PropertiesPage-ListingViewSwitcher" /> :
                                                <IconGridOutline className="PropertiesPage-ListingViewSwitcher" />
                                            }
                                        </Button>
                                    </ButtonGroup>
                                </Spacer>
                            )}
                        </StyledRightSubFlexContainer>

                        {!includeArchived && (
                            <Tabs selected={subNavFilterType}>
                                <StyledTabsList
                                    aria-label="SubNav Filter Tabs"
                                    onSelected={id => this.subNavFiltersOnSelectHandler(subNavFilterOptions[id])}
                                >
                                    {Object.keys(subNavFilterOptions).map(
                                        id => this.renderSubNavButton(subNavFilterOptions[id])
                                    )}
                                </StyledTabsList>
                            </Tabs>
                        )}
                    </StyledFlexContainer>
                )}
            </Container>
        );
    }

    handlePushNotificationClick(event) {
        const button = event.target;

        button.disabled = true;
        button.innerHTML = NotificationModule.isSubscribed() ? '\u2611 Push Notification' : '\u2610 Push Notification';

        // check if subscriptions are registered
        if (NotificationModule.isSubscribed()) {
            // yes subscriptions - unsubscribe
            NotificationModule.unsubscribe()
                .then(() => {
                    // update ui
                    button.innerHTML = '\u2610 Push Notification';
                    button.disabled = false;
                });
        } else {
            // no subscriptions - subscribe
            NotificationModule.subscribe()
                .then(() => {
                    // update ui
                    button.innerHTML = '\u2611 Push Notification';
                    button.disabled = false;
                })
                .catch((ErrorRMW) => {
                    appLogger.logError({
                        message: ErrorRMW,
                        context: 'PropertiesPage.handlePushNotificationClick',
                    });
                });
        }
    }

    renderPushNotificationBtn() {
        let btnTitle = '';
        let isBtnDisabled = false;

        if (NotificationModule.hasNotificationFeature()) {
            // Note: Notification.permission is undefined at runtime.
            if (Notification.permission === 'denied') {
                // with out checkmark
                btnTitle = '\u2610 Push Notification (denied)';
                isBtnDisabled = true;
            } else if (NotificationModule.isSubscribed()) {
                // with checkmark
                btnTitle = '\u2611 Push Notification';
            } else {
                // with out checkmark
                btnTitle = '\u2610 Push Notification';
            }

            return (
                <button
                    type="button"
                    disabled={isBtnDisabled}
                    onClick={this.handlePushNotificationClick}
                >
                    {btnTitle}
                </button>
            );
        }
        return false;
    }

    renderModals() {
        const {
            emailVerification: {
                hasFeedListings,
                hasPendingEmailVerification,
            },
            modalAddProperty,
        } = this.props;
        const { totalListingsWithoutFilters } = this.state;
        const { behavior = 'Add', isOpen } = modalAddProperty;

        return (
            <React.Fragment>
                <AddPropertyModal
                    isOpen={isOpen}
                    behavior={behavior}
                    totalListings={totalListingsWithoutFilters}
                    preventLandingPageRedirect={hasFeedListings && hasPendingEmailVerification}
                />
            </React.Fragment>
        );
    }

    renderListingsLoadComplete() {
        const {
            properties: { listings, pager },
            propertiesFilters: { includeArchived, viewType },
            isScreenTabletOrDesktop,
        } = this.props;
        const {
            displayTimedOutMessage,
            didPropertiesLoad,
            displayNoListingsFoundMessage,
            disableLoadMore,
        } = this.state;

        const isGridView = viewType === PropertiesConstants.PROPERTIES_VIEW_TYPE.GRID;

        return (
            <Container
                fluidMobile={true}
                marginTop={(!isGridView && !isScreenTabletOrDesktop) ? '0' : 'md'}
            >
                <PropertiesDisplay
                    listings={listings}
                    displayTimedOutMessage={displayTimedOutMessage}
                    displayNoListingsFoundMessage={displayNoListingsFoundMessage}
                    didPropertiesLoad={didPropertiesLoad}
                    showArchived={includeArchived}
                    viewType={viewType}
                    onRemoveSearchTextClick={this.onSearchFilterUpdate}
                />

                <Row>
                    <Col>
                        <Container className="InfiniteScrollerLoadMore">
                            <InfiniteScroller
                                pager={pager}
                                text="Load more properties"
                                onLoadMoreClick={this.handleLoadMoreClick}
                                disabled={disableLoadMore}
                            />
                        </Container>
                    </Col>
                </Row>
            </Container>
        );
    }

    getConversionPercentage(count) {
        const conversionRate = listingClaimUtils.getConversionRate(count);
        return numberUtils.asPercent(conversionRate);
    }

    renderLoadComplete() {
        const {
            propertiesFilters: { includeArchived },
            propertyConversion,
            feedClaimAndConvertUpdate,
        } = this.props;

        const {
            displayPaymentsNotification,
            displayApplicationsNotification,
            displayPendingEmailVerificationBox,
            displayLeasesNotification,
            displayEmailVerificationNotification,
            isDebugMode,
            emailVerificationNotificationText,
            displayListingClaimCompleteBanner,
        } = this.state;

        const displayListingConversionBanner = propertyConversion.count.processing > 0
            || propertyConversion.count.pending > 0;
        const displayFeedClaimAndConvertUpdateBanner = feedClaimAndConvertUpdate
            && feedClaimAndConvertUpdate.isTierTwoAndThreeUser
            && !feedClaimAndConvertUpdate.hasCompletedFeedClaimAndConvert;

        return (
            <div>
                {isDebugMode && (
                    <Container
                        fluidMobile={true}
                        marginBottom="sm"
                    >
                        <Row>
                            <Col xs={12}>
                                {this.renderPushNotificationBtn()}
                            </Col>
                        </Row>
                    </Container>
                )}

                {displayFeedClaimAndConvertUpdateBanner && (
                    <Container
                        fluidMobile={true}
                        marginBottom="sm"
                    >
                        <Row>
                            <Col xs={12}>
                                <Banner
                                    body={(
                                        <>
                                            <Text as="div">{feedClaimAndConvertBannerText}</Text>
                                            <Spacer marginTop="xs">
                                                <Anchor href="/rental-manager/listing-claiming">
                                                    Convert your MLS listings
                                                </Anchor>
                                            </Spacer>
                                        </>
                                    )}
                                />
                            </Col>
                        </Row>
                    </Container>
                )}

                {displayListingConversionBanner && (
                    <Container
                        className="PropertiesPage-display-container"
                        fluidMobile={true}
                        marginBottom="sm"
                    >
                        <Row>
                            <Col xs={12}>
                                <ActionMessageBox logoType="computer-screen">
                                    <Text fontType="body">
                                        <b>
                                            {`${this.getConversionPercentage(propertyConversion.count)} complete`}
                                        </b>
                                        &nbsp;
                                        Conversion of your listings is in progress! You can access your listings once
                                        this is complete.
                                    </Text>
                                </ActionMessageBox>
                            </Col>
                        </Row>
                    </Container>
                )}

                {displayListingClaimCompleteBanner && (
                    <Container
                        fluidMobile={true}
                        marginBottom="sm"
                    >
                        <Row>
                            <Col xs={12}>
                                <Banner
                                    appearance="success"
                                    body="Select a property below to view and manage its information."
                                    closeButton={<CloseButton />}
                                    onClose={this.handleNotificationCloseClick}
                                />
                            </Col>
                        </Row>
                    </Container>
                )}

                {displayApplicationsNotification && (
                    <Container
                        fluidMobile={true}
                        marginBottom="sm"
                    >
                        <Row>
                            <Col xs={12}>
                                <Banner
                                    appearance="success"
                                >
                                    <Flex flexGrow={1}>
                                        <Text>
                                            Applications are enabled for your properties. You can change application
                                            preferences anytime on the property&apos;s&nbsp;
                                            <em>Applications</em>
                                            &nbsp;tab.
                                        </Text>
                                    </Flex>
                                    <Flex>
                                        <StyledCloseButton onClick={this.handlePaymentsNotificationCloseClick} />
                                    </Flex>
                                </Banner>
                            </Col>
                        </Row>
                    </Container>
                )}

                {displayPaymentsNotification && (
                    <Container
                        fluidMobile={true}
                        marginBottom="sm"
                    >
                        <Row>
                            <Col xs={12}>
                                <Banner
                                    appearance="success"
                                >
                                    <Flex flexGrow={1}>
                                        <Text>
                                            To manage your rent payments, click on&nbsp;
                                            <em>Payments</em>
                                            &nbsp;for any property below.
                                        </Text>
                                    </Flex>
                                    <Flex>
                                        <StyledCloseButton onClick={this.handlePaymentsNotificationCloseClick} />
                                    </Flex>
                                </Banner>
                            </Col>
                        </Row>
                    </Container>
                )}

                {displayEmailVerificationNotification && (
                    <Container
                        fluidMobile={true}
                        marginBottom="sm"
                    >
                        <Row>
                            <Col xs={12}>
                                <Banner
                                    appearance="success"
                                >
                                    <Flex flexGrow={1}>
                                        <Text>
                                            {emailVerificationNotificationText}
                                        </Text>
                                    </Flex>
                                    <Flex>
                                        <StyledCloseButton
                                            onClick={this.handleEmailVerificationNotificationCloseClick}
                                        />
                                    </Flex>
                                </Banner>
                            </Col>
                        </Row>
                    </Container>
                )}

                {displayPendingEmailVerificationBox && (
                    <Container
                        fluidMobile={true}
                        marginBottom="sm"
                    >
                        <Row>
                            <Col>
                                <PendingEmailVerificationBox
                                    handleResendEmailClick={this.handleResendEmailClick}
                                />
                            </Col>
                        </Row>
                    </Container>
                )}

                {displayLeasesNotification && (
                    <Container
                        fluidMobile={true}
                        marginBottom="sm"
                    >
                        <Row>
                            <Col xs={12}>
                                <Banner
                                    appearance="success"
                                >
                                    <Flex flexGrow={1}>
                                        <Text>
                                            For your properties with a&nbsp;
                                            <em>Leases</em>
                                            &nbsp;tab, click on it to create a custom lease.
                                        </Text>
                                    </Flex>
                                    <Flex>
                                        <StyledCloseButton onClick={this.handleLeasesNotificationCloseClick} />
                                    </Flex>
                                </Banner>
                            </Col>
                        </Row>
                    </Container>
                )}
                {this.renderListingsLoadComplete()}
                <Container>
                    <Spacer marginY="sm">
                        <TextButton onClick={this.toggleArchivedView}>
                            {`View ${includeArchived ? 'for rent & off market' : 'archived'} properties`}
                        </TextButton>
                    </Spacer>
                </Container>
            </div>
        );
    }

    render() {
        const { brand, paths } = this.context;

        const {
            maintenancePartner,
            propertiesFilters: { includeArchived, viewType },
            isUserBlocked,
        } = this.props;

        const { listingsLoading } = this.state;

        const pageTitle = includeArchived ? 'Archived Properties' : 'Properties';
        const displayMaintenanceBanner = brand === Constants.UNFORMATTED_BRAND_NAMES.zillow &&
            maintenancePartner && maintenancePartner.isAvailable;

        if (this.needsRedirect() && !isUserBlocked) {
            return <Redirect to={paths.base} />;
        }

        return (
            <AuthCheckWrapper requireStrongAuthentication={false}>
                <div className="PropertiesPage">
                    <HeadWrapper
                        title={pageTitle}
                    />

                    {!listingsLoading && this.renderModals()}

                    {this.renderPageHeading()}
                    {this.renderSubHeadingSection()}

                    {listingsLoading ?
                        <PropertiesPageSkeleton viewType={viewType} /> : this.renderLoadComplete()
                    }

                    {displayMaintenanceBanner &&
                        this.renderMaintenanceBanner()
                    }
                </div>
            </AuthCheckWrapper>
        );
    }
}

const mapStateToProps = state => ({
    appConfig: AppSelectors.makeAppConfig(state),
    cookieEnabledServices: AppSelectors.makeCookieEnabledServices(state),
    currentProperty: PropertiesSelectors.makeCurrentProperty(state),
    emailVerification: EmailVerificationSelectors.makeEmailVerification(state),
    feedClaimAndConvertUpdate: PropertiesSelectors.makeFeedClaimAndConvertUpdate(state),
    hasSubNavFiltersBeenTouched: state.properties.properties.hasSubNavFiltersBeenTouched,
    isScreenTabletOrDesktop: AppSelectors.makeIsScreenTabletOrDesktop(state),
    isEnterpriseCustomer: state.user.isEnterpriseCustomer,
    isUserBlocked: UserSelectors.makeIsUserBlocked(state),
    location: RoutingSelectors.makeLocation(state),
    maintenancePartner: PropertiesSelectors.makeMaintenancePartner(state),
    modalAddProperty: ModalSelectors.makeAddPropertyModal(state),
    pager: PropertiesSelectors.makePager(state),
    prevPath: AppSelectors.makePrevPath(state),
    properties: PropertiesSelectors.makeProperties(state),
    propertiesFilters: PropertiesSelectors.makePropertiesFilters(state),
    propertyConversion: PropertiesSelectors.makePropertyConversion(state),
    roles: UserSelectors.makeRoles(state),
    user: UserSelectors.makeUser(state),
});

export { PropertiesPage };
export default withErrorBoundary(connect(mapStateToProps)(PropertiesPage));
